import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Check, ShieldCheck, ArrowLeft, Loader2, BadgeIndianRupee, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const TIER_ORDER = ["single", "pro", "studio"];
const DUR_LABEL = { 1: "Monthly", 6: "6 Months", 12: "Yearly" };

export default function Subscribe() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [tier, setTier] = useState("pro");
  const [duration, setDuration] = useState(1);
  const [rewards, setRewards] = useState({ days_credit: 0, successful_referrals: 0 });
  const [loading, setLoading] = useState(false);
  const returnTo = location.state?.returnTo || "/dashboard";

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
    api.get("/subscription").then(({ data }) => {
      setRewards(data.referral_rewards || { days_credit: 0 });
      if (data.plan?.plan_id) {
        const cur = data.plan;
        setTier(cur.tier || "pro");
      }
    }).catch(() => {});
  }, []);

  const activeTier = user?.plan?.tier;
  const activePlanId = user?.plan?.plan_id;
  const selected = plans.find((p) => p.tier === tier && p.duration_months === duration);

  const subscribe = async (planId) => {
    setLoading(true);
    try {
      await api.post("/subscription/subscribe", { plan_id: planId });
      await refreshUser();
      toast.success("Plan active. UPI AutoPay set up (demo).");
      navigate(returnTo);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not activate plan");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen"
      style={{ background: "radial-gradient(ellipse at top, rgba(201,162,75,0.1), transparent 55%), #0A0A0A" }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white inline-flex items-center gap-1.5 text-sm mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex justify-center mb-6"><Logo size={44} /></div>
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Pick a plan that fits your shops</h1>
          <p className="text-zinc-400 text-sm mt-2">Per-site pricing. Bundle more sites to save. Longer term = bigger discount.</p>
        </div>

        {rewards.days_credit > 0 && (
          <div data-testid="subscribe-referral-banner" className="mb-6 rounded-2xl border border-[#C9A24B]/30 bg-[#C9A24B]/10 p-4 flex items-center gap-3">
            <Gift className="w-5 h-5 text-[#C9A24B]" />
            <div className="text-sm">
              <b className="text-[#E5C378]">{rewards.days_credit} bonus days</b> from your referrals — auto-applied on this plan.
            </div>
          </div>
        )}

        {/* Duration toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-2xl border border-[#27272A] p-1 bg-[#161616]">
            {[1, 6, 12].map((m) => (
              <button key={m} data-testid={`subscribe-duration-${m}`} onClick={() => setDuration(m)}
                className={`px-4 sm:px-5 py-2 text-sm font-semibold rounded-xl transition-all ${duration === m ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-300 hover:text-white"}`}>
                {DUR_LABEL[m]}{m === 12 && <span className="ml-2 text-[10px] text-[#22C55E]">Save 20%</span>}
                {m === 6 && <span className="ml-2 text-[10px] text-[#22C55E]">Save 15%</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {TIER_ORDER.map((t) => {
            const p = plans.find((x) => x.tier === t && x.duration_months === duration);
            if (!p) return <div key={t} className="rounded-3xl border border-[#27272A] bg-[#161616] p-6 min-h-[380px] animate-pulse" />;
            const isActive = activePlanId === p.id;
            const isSelected = tier === t;
            return (
              <button key={t} data-testid={`subscribe-plan-${t}`}
                onClick={() => setTier(t)}
                className={`text-left rounded-3xl border p-6 transition-all relative ${isSelected ? "border-[#C9A24B] bg-[#C9A24B]/5" : "border-[#27272A] bg-[#161616] hover:border-[#3a3a3d]"} ${t === "pro" ? "md:scale-[1.02]" : ""}`}>
                {t === "pro" && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A24B] text-[#0A0A0A] text-[10px] font-bold px-3 py-1 rounded-full">MOST POPULAR</span>
                )}
                {isActive && (
                  <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/30 px-2 py-0.5 rounded-full">Current</span>
                )}
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-1">{p.tier_label}</div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-4xl font-bold">₹{p.price.toLocaleString("en-IN")}</span>
                  {duration > 1 && <span className="text-zinc-500 text-sm line-through">₹{p.sticker_price.toLocaleString("en-IN")}</span>}
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {duration === 1 ? "per month" : `for ${duration} months · effective ₹${p.monthly_effective}/mo`}
                </p>
                <div className="mt-4 text-sm font-semibold text-[#E5C378]">
                  {p.slots} website{p.slots > 1 ? "s" : ""}
                </div>
                <div className="mt-5 space-y-2.5">
                  {p.features.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm">
                      <span className="w-4 h-4 rounded-full bg-[#C9A24B]/15 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5 text-[#C9A24B]" />
                      </span>
                      <span className="text-zinc-300">{f}</span>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Checkout */}
        {selected && (
          <div className="bg-[#161616] border border-[#27272A] rounded-3xl p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">You've selected</div>
              <div className="font-display text-lg font-bold mt-0.5">
                {selected.tier_label} · {DUR_LABEL[duration]} — {selected.slots} site{selected.slots > 1 ? "s" : ""}
              </div>
              <div data-testid="subscribe-checkout-total" className="text-sm text-zinc-400">Total ₹{selected.price.toLocaleString("en-IN")} · Cancel anytime · UPI AutoPay</div>
            </div>
            <button data-testid="subscribe-checkout-btn" onClick={() => subscribe(selected.id)} disabled={loading || activePlanId === selected.id}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] font-bold px-6 py-3.5 rounded-xl active:scale-[0.98] transition-all disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 wt-spin" /> : <BadgeIndianRupee className="w-5 h-5" />}
              {activePlanId === selected.id ? "Currently Active" : `Pay ₹${selected.price.toLocaleString("en-IN")} via UPI`}
            </button>
          </div>
        )}

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500">
          <ShieldCheck className="w-4 h-4 text-[#C9A24B]" /> Demo payment — no real money charged
        </div>
      </div>
    </div>
  );
}
